import axiosInstance from '@/lib/enhancedAxiosInstance';
import { paths } from '@/lib/apiPaths';
import { toast } from 'sonner';
import { logError } from '@/utils/errorHandler';

/**
 * Interface for Locale objects returned from the API
 */
export interface Locale {
  id: number;
  code: string;
  label: string;
  description?: string;
  name?: string;
  is_active: boolean;
}

/**
 * Helper function to normalize locale data
 * @param raw The raw locale data
 * @returns Normalized locale data
 */
const normalizeLocale = (raw: Locale | null | undefined): Locale => {
  if (!raw) {
    return {
      id: 0,
      code: '',
      label: '',
      is_active: false,
    };
  }

  // Ensure all required fields are present
  return {
    ...raw,
    id: raw.id || 0,
    code: raw.code || '',
    label: raw.label || raw.description || raw.name || raw.code || 'Unknown Locale',
    is_active: typeof raw.is_active === 'boolean' ? raw.is_active : true,
  };
};

/**
 * Service for managing organization locales
 */
const localeService = {
  /**
   * Get all locales for the current organization
   * @returns Promise resolving to an array of Locale objects
   */
  getLocales: async (): Promise<Locale[]> => {
    try {
      const localesUrl = paths.locales.root();

      const response = await axiosInstance.get(localesUrl);
      const data = response.data;

      // Normalize the data to ensure consistent format
      let locales: Locale[] = [];

      if (Array.isArray(data)) {
        locales = data.map(locale => normalizeLocale(locale));
      } else if (data && typeof data === 'object') {
        // In case the API returns an object with results property
        const results = data.results || data.items || [];
        locales = Array.isArray(results) ? results.map(locale => normalizeLocale(locale)) : [];
      } else {
        logError(
          'Unexpected locales data format',
          {
            component: 'localeService',
            action: 'getLocales',
            metadata: { data, dataType: typeof data },
          },
          { showToast: false, userFacing: false }
        );
      }

      // Deduplicate by locale code to avoid duplicate options in dropdowns
      const uniqueLocales = Array.from(new Map(locales.map(l => [l.code, l])).values());

      return uniqueLocales;
    } catch (error) {
      logError(
        'Failed to fetch locales',
        {
          component: 'localeService',
          action: 'getLocales',
          metadata: { error },
        },
        { showToast: false, userFacing: false }
      );
      return [];
    }
  },

  /**
   * Get a specific locale by code
   * @param code The locale code
   * @returns Promise with the locale or null if not found
   */
  getLocale: async (code: string): Promise<Locale | null> => {
    try {
      const locales = await localeService.getLocales();
      const locale = locales.find(l => l.code === code);
      return locale ? normalizeLocale(locale) : null;
    } catch (error) {
      logError(
        'Failed to fetch locale by code',
        {
          component: 'localeService',
          action: 'getLocale',
          metadata: { code, error },
        },
        { showToast: false, userFacing: false }
      );
      return null;
    }
  },

  /**
   * Create a new locale for the current organization
   * @param code The locale code (e.g., 'en_US', 'fr_FR')
   * @param label The human-readable label (e.g., 'English (US)', 'French')
   * @param toastLabels Toast messages from translations hook
   * @returns Promise resolving to the created Locale object
   */
  createLocale: async (code: string, label: string, toastLabels?: any): Promise<Locale> => {
    try {
      const { data } = await axiosInstance.post(paths.locales.root(), {
        code,
        label,
        is_active: true,
      });
      const successMessage = toastLabels?.locales?.createSuccess || 'Locale created successfully';
      toast.success(successMessage);
      return data;
    } catch (error) {
      logError(
        'Failed to create locale',
        {
          component: 'localeService',
          action: 'createLocale',
          metadata: { code, label, error },
        },
        { showToast: false, userFacing: false }
      );
      const errorMessage = toastLabels?.locales?.createError || 'Failed to create locale';
      toast.error(errorMessage);
      throw error;
    }
  },

  /**
   * Update an existing locale
   * @param id The ID of the locale to update
   * @param changes Object containing the fields to update (code, label, or is_active)
   * @param toastLabels Toast messages from translations hook
   * @returns Promise resolving to the updated Locale object
   */
  updateLocale: async (
    id: number,
    changes: Partial<{ code: string; label: string; is_active: boolean }>,
    toastLabels?: any
  ): Promise<Locale> => {
    try {
      const { data } = await axiosInstance.patch(paths.locales.byId(id), changes);
      const successMessage = toastLabels?.locales?.updateSuccess || 'Locale updated successfully';
      toast.success(successMessage);
      return data;
    } catch (error) {
      logError(
        'Failed to update locale',
        {
          component: 'localeService',
          action: 'updateLocale',
          metadata: { id, changes, error },
        },
        { showToast: false, userFacing: false }
      );
      const errorMessage = toastLabels?.locales?.updateError || 'Failed to update locale';
      toast.error(errorMessage);
      throw error;
    }
  },

  /**
   * Delete a locale
   * @param id The ID of the locale to delete
   * @param toastLabels Toast messages from translations hook
   * @returns Promise that resolves when the locale is deleted
   */
  deleteLocale: async (id: number, toastLabels?: any): Promise<void> => {
    try {
      await axiosInstance.delete(paths.locales.byId(id));
      const successMessage = toastLabels?.locales?.deleteSuccess || 'Locale deleted successfully';
      toast.success(successMessage);
    } catch (error) {
      logError(
        'Failed to delete locale',
        {
          component: 'localeService',
          action: 'deleteLocale',
          metadata: { id, error },
        },
        { showToast: false, userFacing: false }
      );
      const errorMessage = toastLabels?.locales?.deleteError || 'Failed to delete locale';
      toast.error(errorMessage);
      throw error;
    }
  },
};

export default localeService;
